export type CaseNotesSaveEligibility =
  | 'unchanged'
  | 'appendable'
  | 'not_appendable';

/** Normalize line endings so prefix checks work across platforms. */
export function normalizeCaseNotes(text: string): string {
  return text.replace(/\r\n/g, '\n');
}

/** Whether `next` keeps last-saved notes intact and only adds text after them. */
export function isCaseNotesAppendOnlyChange(
  lastSaved: string | null | undefined,
  next: string
): boolean {
  return normalizeCaseNotes(next).startsWith(
    normalizeCaseNotes(lastSaved ?? '')
  );
}

/** Suffix the user is adding on top of last-saved notes. */
export function getCaseNotesAppendSuffix(
  lastSaved: string | null | undefined,
  current: string
): string {
  const saved = normalizeCaseNotes(lastSaved ?? '');
  const normalized = normalizeCaseNotes(current);
  if (!normalized.startsWith(saved)) return '';
  return normalized.slice(saved.length);
}

/** Rebuild full notes from saved baseline + append-only input. */
export function buildCaseNotesWithAppend(
  lastSaved: string | null | undefined,
  append: string
): string {
  return normalizeCaseNotes(lastSaved ?? '') + append;
}

/** Whether current notes can be persisted to an append-only API. */
export function getCaseNotesSaveEligibility(
  lastSaved: string | null | undefined,
  current: string
): CaseNotesSaveEligibility {
  const saved = normalizeCaseNotes(lastSaved ?? '');
  const normalized = normalizeCaseNotes(current);
  if (normalized === saved) return 'unchanged';
  return getCaseNotesAppendDelta(lastSaved, current) !== null
    ? 'appendable'
    : 'not_appendable';
}

/**
 * Returns the suffix to send to an append-only case notes API.
 * Returns null when there is nothing new to send, or when `current` is not
 * a strict extension of `lastSaved` (edits or deletions in existing content).
 */
export function getCaseNotesAppendDelta(
  lastSaved: string | null | undefined,
  current: string
): string | null {
  const append = getCaseNotesAppendSuffix(lastSaved, current);
  return append.length > 0 ? append : null;
}
